import { REPORT_DASHBOARD_FORMS as STATIC_REPORT_DASHBOARD_FORMS, REPORT_IDS, REPORT_REGISTRY as STATIC_REPORT_REGISTRY, } from './report-dashboard-registry.js';
import { CLIENT_REPORTS_EXPORT_OWN, CLIENT_REPORTS_READ_OWN, } from '../operations/operation-ids.js';
import { CLIENT_GOVERNANCE_READ, REPORT_EXPORT } from '../operations/index.js';
import { PLATFORM_REPORT_DASHBOARD_FORMS, PLATFORM_REPORT_REGISTRY, } from './platform-report-registry.js';
import { PORTFOLIO_CUSTODY_EVIDENCE_REPORT_FORMS, PORTFOLIO_CUSTODY_EVIDENCE_REPORT_REGISTRY, } from './portfolio-custody-report-registry.js';
import { PORTFOLIO_PERFORMANCE_REPORT_FORMS, PORTFOLIO_PERFORMANCE_REPORT_REGISTRY, } from './portfolio-performance-report-registry.js';
import { PMS_ANALYTICAL_REPORT_FORMS, PMS_ANALYTICAL_REPORT_REGISTRY, } from './pms-analytical-report-registry.js';
import { PMS_OPERATIONAL_REPORT_FORMS, PMS_OPERATIONAL_REPORT_REGISTRY, } from './pms-operational-report-registry.js';
import { addReportAuthorityDefinitionFields } from './report-authority-definition-form.js';
import { withCanonicalFieldAnnotationsRegistry } from './descriptors/form-field-annotations.js';
export const ORGANIZATION_CLIENT_STATEMENT_REPORT_ID = 'report.organization.client-statements';
const ORGANIZATION_CLIENT_STATEMENT_FORM_ID = 'form.report.organizationClientStatement.parameters';
const V1_LIVE_QUERY_POLICY = Object.freeze({
    cache: 'NO_STORE',
    refresh: 'ON_DEMAND',
    synchronousExportMaxRows: 10_000,
});
export const IMPLEMENTED_REGULATORY_REPORT_TYPES = Object.freeze([
    'SEBON_MONTHLY',
    'IRD_CGT',
    'NRB_QUARTERLY',
    'FIU_AML_CTF',
]);
const V1_STATIC_REPORT_IDS = new Set([
    REPORT_IDS.PORTFOLIO_HOLDINGS,
    REPORT_IDS.CLIENT_HOLDINGS,
    REPORT_IDS.ACCOUNTING_TRIAL_BALANCE,
    REPORT_IDS.ACCOUNTING_FINANCIAL_STATEMENTS,
    REPORT_IDS.ACCOUNTING_PROFIT_AND_LOSS,
    REPORT_IDS.RECONCILIATION_BREAKS,
    REPORT_IDS.TRANSACTION_ACTIVITY,
    REPORT_IDS.CLIENT_STATEMENT,
    REPORT_IDS.SEBON_MONTHLY,
    REPORT_IDS.IRD_CGT,
    REPORT_IDS.NRB_QUARTERLY,
    REPORT_IDS.FIU_AML_CTF,
]);
const V1_STATIC_CALCULATION_VERSIONS = Object.freeze({
    [REPORT_IDS.PORTFOLIO_HOLDINGS]: 'holdings-snapshot-v1-latest-per-key',
    [REPORT_IDS.CLIENT_HOLDINGS]: 'holdings-snapshot-v1-latest-per-key',
    [REPORT_IDS.ACCOUNTING_TRIAL_BALANCE]: 'trial-balance-v1-debit-positive',
    [REPORT_IDS.ACCOUNTING_FINANCIAL_STATEMENTS]: 'financial-statements-v1-posted-ledger-direct-cash-flow',
    [REPORT_IDS.ACCOUNTING_PROFIT_AND_LOSS]: 'profit-and-loss-v1-revenue-expense-netted',
    [REPORT_IDS.RECONCILIATION_BREAKS]: 'reconciliation-exposure-v1',
    [REPORT_IDS.TRANSACTION_ACTIVITY]: 'transaction-activity-v1-latest-valuation-per-position',
    [REPORT_IDS.CLIENT_STATEMENT]: 'client-statement-v1',
    [REPORT_IDS.SEBON_MONTHLY]: 'regulatory-v1',
    [REPORT_IDS.IRD_CGT]: 'regulatory-v1',
    [REPORT_IDS.NRB_QUARTERLY]: 'regulatory-v1',
    [REPORT_IDS.FIU_AML_CTF]: 'regulatory-v1',
});
const effectiveStaticReports = Object.fromEntries(Object.entries(STATIC_REPORT_REGISTRY)
    .filter(([id]) => V1_STATIC_REPORT_IDS.has(id))
    .map(([id, descriptor]) => [
    id,
    {
        ...descriptor,
        implemented: true,
        lineage: {
            ...descriptor.lineage,
            calculationVersion: V1_STATIC_CALCULATION_VERSIONS[id] ?? descriptor.lineage?.calculationVersion,
        },
    },
]));
function hardenFinancialStatementHierarchy() {
    const report = effectiveStaticReports[REPORT_IDS.ACCOUNTING_FINANCIAL_STATEMENTS];
    if (!report)
        throw new Error('Financial statements report descriptor is required');
    const statement = report.columns.find((column) => column.field === 'statement');
    const name = report.columns.find((column) => column.field === 'name');
    if (!statement || !name)
        throw new Error('Financial statements grouping columns are required');
    const accountGroup = {
        ...name,
        id: 'column.report.financialStatements.accountGroupName',
        field: 'accountGroupName',
        labelKey: 'reports.columns.accountGroupName',
        accessibilityLabelKey: 'reports.columns.accountGroupName',
        telemetryId: 'experience.column.report.financialStatements.accountGroupName',
        groupable: true,
        groupKey: 'accountGroupName',
        defaultVisible: false,
        requiredVisible: false,
    };
    effectiveStaticReports[REPORT_IDS.ACCOUNTING_FINANCIAL_STATEMENTS] = {
        ...report,
        columns: [
            { ...statement, groupable: true, groupKey: 'statement' },
            accountGroup,
            ...report.columns.filter((column) => column.field !== 'statement'),
        ],
        // The generic v1 table supports one active grouping dimension. Keep the
        // default truthful until recursive grouping is implemented generically.
        defaultGrouping: [{ field: 'statement', direction: 'asc' }],
    };
}
hardenFinancialStatementHierarchy();
const staticClientStatement = effectiveStaticReports[REPORT_IDS.CLIENT_STATEMENT];
if (!staticClientStatement)
    throw new Error('Client statement report descriptor is required');
const staticClientStatementForm = STATIC_REPORT_DASHBOARD_FORMS[String(staticClientStatement.parameterFormId)];
if (!staticClientStatementForm)
    throw new Error('Client statement parameter form is required');
effectiveStaticReports[REPORT_IDS.CLIENT_STATEMENT] = {
    ...staticClientStatement,
    scope: 'CLIENT',
    operationId: CLIENT_REPORTS_READ_OWN,
    columns: staticClientStatement.columns.map((column) => ({
        ...column,
        operationId: CLIENT_REPORTS_READ_OWN,
        privacyClassification: 'FINANCIAL',
    })),
    operationBindings: {
        listOperationId: CLIENT_REPORTS_READ_OWN,
        runOperationId: CLIENT_REPORTS_READ_OWN,
        exportOperationId: CLIENT_REPORTS_EXPORT_OWN,
    },
};
const organizationClientStatementForm = {
    ...staticClientStatementForm,
    id: ORGANIZATION_CLIENT_STATEMENT_FORM_ID,
    operationId: CLIENT_GOVERNANCE_READ,
    titleKey: 'reports.organizationClientStatement.parametersTitle',
    descriptionKey: 'reports.organizationClientStatement.parametersDescription',
    fields: staticClientStatementForm.fields
        .filter((field) => ['clientAccountId', 'portfolioAccountId', 'periodStart', 'periodEnd'].includes(field.name))
        .map((field) => ({ ...field })),
    sections: undefined,
    actions: [],
    submission: {
        ...staticClientStatementForm.submission,
        includeFields: staticClientStatementForm.fields
            .filter((field) => ['clientAccountId', 'portfolioAccountId', 'periodStart', 'periodEnd'].includes(field.name))
            .map((field) => field.id),
        excludeFields: undefined,
    },
};
const organizationClientStatementReport = {
    ...staticClientStatement,
    id: ORGANIZATION_CLIENT_STATEMENT_REPORT_ID,
    titleKey: 'reports.types.organizationClientStatement',
    descriptionKey: 'reports.descriptions.organizationClientStatement',
    scope: 'BRANCH',
    operationId: CLIENT_GOVERNANCE_READ,
    parameterFormId: ORGANIZATION_CLIENT_STATEMENT_FORM_ID,
    runFormId: ORGANIZATION_CLIENT_STATEMENT_FORM_ID,
    columns: staticClientStatement.columns.map((column) => ({
        ...column,
        operationId: CLIENT_GOVERNANCE_READ,
        privacyClassification: 'FINANCIAL',
    })),
    operationBindings: {
        listOperationId: CLIENT_GOVERNANCE_READ,
        runOperationId: CLIENT_GOVERNANCE_READ,
        exportOperationId: REPORT_EXPORT,
    },
    auditPolicy: 'SUCCESS_ONLY',
};
const candidateEffectiveReports = {
    ...effectiveStaticReports,
    [ORGANIZATION_CLIENT_STATEMENT_REPORT_ID]: organizationClientStatementReport,
    ...PMS_ANALYTICAL_REPORT_REGISTRY,
    ...PMS_OPERATIONAL_REPORT_REGISTRY,
    ...PLATFORM_REPORT_REGISTRY,
    ...PORTFOLIO_PERFORMANCE_REPORT_REGISTRY,
    ...PORTFOLIO_CUSTODY_EVIDENCE_REPORT_REGISTRY,
};
/** Every production report has explicit audit and live-query execution policy at the effective registry boundary. */
const effectiveReports = Object.freeze(Object.fromEntries(Object.entries(candidateEffectiveReports).map(([id, descriptor]) => [
    id,
    {
        ...descriptor,
        auditPolicy: descriptor.auditPolicy ?? 'SUCCESS_ONLY',
        ...(descriptor.executionMode === 'LIVE_QUERY'
            ? { liveQueryPolicy: descriptor.liveQueryPolicy ?? V1_LIVE_QUERY_POLICY }
            : {}),
    },
])));
const allCandidateForms = {
    ...STATIC_REPORT_DASHBOARD_FORMS,
    [ORGANIZATION_CLIENT_STATEMENT_FORM_ID]: organizationClientStatementForm,
    ...PMS_ANALYTICAL_REPORT_FORMS,
    ...PMS_OPERATIONAL_REPORT_FORMS,
    ...PLATFORM_REPORT_DASHBOARD_FORMS,
    ...PORTFOLIO_PERFORMANCE_REPORT_FORMS,
    ...PORTFOLIO_CUSTODY_EVIDENCE_REPORT_FORMS,
};
const referencedFormIds = new Set(Object.values(effectiveReports).flatMap((report) => [report.parameterFormId, report.definitionFormId, report.runFormId]
    .filter((value) => Boolean(value))
    .map(String)));
const v1Forms = Object.fromEntries(Object.entries(allCandidateForms)
    .filter(([id]) => referencedFormIds.has(id))
    .map(([id, form]) => [id, addReportAuthorityDefinitionFields(form)]));
const TRANSACTION_ACTIVITY_STATUS_OPTIONS = [
    ['DRAFT', 'reports.filters.status.draft'],
    ['SUBMITTED', 'reports.filters.status.submitted'],
    ['PENDING_APPROVAL', 'reports.filters.status.pendingApproval'],
    ['APPROVED', 'reports.filters.status.approved'],
    ['REJECTED', 'reports.filters.status.rejected'],
    ['WITHDRAWN', 'reports.filters.status.withdrawn'],
    ['POSTED', 'reports.filters.status.posted'],
    ['REVERSED', 'reports.filters.status.reversed'],
    ['ROLLED_BACK', 'reports.filters.status.rolledBack'],
];
const TRANSACTION_ACTIVITY_TYPE_OPTIONS = [
    ['OPENING_BALANCE', 'reports.filters.type.openingBalance'],
    ['PURCHASE', 'reports.filters.type.buy'],
    ['SALE', 'reports.filters.type.sell'],
    ['BONUS_SHARE', 'reports.filters.type.bonusShare'],
    ['RIGHT_SHARE', 'reports.filters.type.rightShare'],
    ['SPLIT', 'reports.filters.type.split'],
    ['CONSOLIDATION', 'reports.filters.type.consolidation'],
    ['MERGER', 'reports.filters.type.merger'],
    ['TRANSFER_IN', 'reports.filters.type.transferIn'],
    ['TRANSFER_OUT', 'reports.filters.type.transferOut'],
    ['FEE', 'reports.filters.type.fee'],
    ['TAX', 'reports.filters.type.tax'],
];
function normalizeTransactionActivityForm(form) {
    return {
        ...form,
        fields: form.fields.map((field) => {
            if (field.name === 'status') {
                return { ...field, options: TRANSACTION_ACTIVITY_STATUS_OPTIONS.map(([value, labelKey]) => ({ value, labelKey: labelKey })) };
            }
            if (field.name === 'type') {
                return { ...field, options: TRANSACTION_ACTIVITY_TYPE_OPTIONS.map(([value, labelKey]) => ({ value, labelKey: labelKey })) };
            }
            return field;
        }),
    };
}
function normalizeClientStatementForm(form) {
    const fields = form.fields.filter((field) => ['periodStart', 'periodEnd'].includes(field.name));
    const fieldIds = new Set(fields.map((field) => String(field.id)));
    return {
        ...form,
        operationId: CLIENT_REPORTS_READ_OWN,
        titleKey: 'reports.clientStatement.parametersTitle',
        descriptionKey: 'reports.clientStatement.parametersDescription',
        fields,
        sections: form.sections
            ?.map((section) => ({ ...section, fields: section.fields.filter((fieldId) => fieldIds.has(String(fieldId))) }))
            .filter((section) => section.fields.length > 0),
        actions: form.actions.map((action) => ({ ...action, operationId: CLIENT_REPORTS_READ_OWN })),
        submission: { ...form.submission, includeFields: fields.map((field) => field.id), excludeFields: undefined },
    };
}
const transactionActivity = effectiveReports[REPORT_IDS.TRANSACTION_ACTIVITY];
for (const rawFormId of [transactionActivity?.parameterFormId, transactionActivity?.runFormId]) {
    if (!rawFormId)
        continue;
    const id = String(rawFormId);
    if (v1Forms[id])
        v1Forms[id] = normalizeTransactionActivityForm(v1Forms[id]);
}
const clientStatement = effectiveReports[REPORT_IDS.CLIENT_STATEMENT];
for (const rawFormId of [clientStatement?.parameterFormId, clientStatement?.runFormId]) {
    if (!rawFormId)
        continue;
    const id = String(rawFormId);
    if (v1Forms[id])
        v1Forms[id] = normalizeClientStatementForm(v1Forms[id]);
}
export const EFFECTIVE_REPORT_DASHBOARD_FORMS = withCanonicalFieldAnnotationsRegistry(v1Forms);
export const EFFECTIVE_REPORT_REGISTRY = effectiveReports;
export function isImplementedRegulatoryReportType(value) {
    return typeof value === 'string' && IMPLEMENTED_REGULATORY_REPORT_TYPES.includes(value);
}
